import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { fetchBlogArticle, formatBlogDate, BLOG_COPY } from "@/lib/blogApi";
import { hero } from "@/lib/images";
import PageShell from "@/components/PageShell";
import Breadcrumbs from "@/components/Breadcrumbs";
import Seo, { breadcrumbSchema } from "@/components/Seo";

const ORIGIN = "https://bookandsmiles.com";

const NotFound = ({ c }) => (
  <section data-testid="blog-not-found" className="min-h-[60vh] flex items-center px-6 lg:px-10 pt-32 pb-20">
    <div className="max-w-3xl mx-auto">
      <p className="eyebrow">404</p>
      <h1 className="font-serif mt-6 text-4xl sm:text-5xl text-stone-900 leading-tight">{c.notFoundTitle}</h1>
      <p className="mt-6 text-stone-600">{c.notFoundText}</p>
      <Link to="/blog" data-testid="blog-back-link" className="mt-10 inline-flex items-center gap-2 text-sm text-stone-800 border-b border-stone-300 hover:border-stone-900 pb-1 transition-colors">
        {c.back} <ArrowRight size={13} />
      </Link>
    </div>
  </section>
);

export default function BlogArticle() {
  const { slug } = useParams();
  const { lang } = useLang();
  const c = BLOG_COPY[lang];
  const [state, setState] = useState({ status: "loading", article: null });

  useEffect(() => {
    setState({ status: "loading", article: null });
    fetchBlogArticle(slug)
      .then((a) => setState({ status: a ? "ok" : "missing", article: a }))
      .catch(() => setState({ status: "missing", article: null }));
  }, [slug]);

  const a = state.article;

  const crumbs = useMemo(
    () => (a ? [{ label: c.home, to: "/" }, { label: c.blog, to: "/blog" }, { label: a.title, to: `/blog/${a.slug}` }] : []),
    [a, c.home, c.blog]
  );

  const schema = useMemo(() => {
    if (!a) return null;
    const article = a.json_ld || {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.title,
      description: a.meta_description,
      datePublished: a.published_at,
      dateModified: a.updated_at,
      inLanguage: a.language,
      author: { "@type": "Organization", name: "Book & Smile" },
      publisher: { "@id": `${ORIGIN}/#business` },
      mainEntityOfPage: `${ORIGIN}/blog/${a.slug}`,
    };
    return [article, a.faq_json_ld, breadcrumbSchema(crumbs)].filter(Boolean);
  }, [a, crumbs]);

  if (state.status === "loading") {
    return (
      <PageShell headerLight={false}>
        <div data-testid="blog-article-loading" className="min-h-[60vh] flex items-center justify-center text-stone-400 text-sm">{c.loading}…</div>
      </PageShell>
    );
  }

  if (!a) {
    return (
      <PageShell headerLight={false}>
        <Seo title={`${c.notFoundTitle} | Book & Smile`} description={c.notFoundText} noindex />
        <NotFound c={c} />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Seo title={`${a.title} | Book & Smile`} description={a.meta_description || c.seoDesc} schema={schema} />
      <article lang={a.language || undefined}>
        <header data-testid="blog-article-hero" className="relative flex items-end overflow-hidden bg-slate-900 min-h-[60vh]">
          <img src={a.hero_image_url || hero("cafeLaptop")} alt={a.hero_image_alt || a.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/55 to-slate-950/30" />
          <div className="relative w-full max-w-4xl mx-auto px-6 lg:px-10 pt-40 pb-16 sm:pb-20">
            <Breadcrumbs items={crumbs.slice(0, 2)} light />
            <p className="eyebrow mt-8 !text-stone-300 fade-up">{formatBlogDate(a.published_at, lang)}</p>
            <h1 data-testid="blog-article-title" className="font-serif mt-6 text-4xl sm:text-5xl lg:text-6xl font-light text-stone-50 leading-[1.15] sm:leading-[1.08] fade-up fade-up-delay-1">
              {a.title}
            </h1>
          </div>
        </header>

        <div className="px-6 lg:px-10 pt-16 pb-20">
          {/* content_html is sanitized server-side with nh3 before storage */}
          <div data-testid="blog-article-body" className="blog-prose max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: a.content_html }} />
        </div>

        <section className="bg-stone-100 border-y border-stone-200 py-16 sm:py-20 px-6 lg:px-10">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
            <Link data-testid="blog-article-cta" to="/contact" className="inline-flex items-center gap-3 bg-slate-900 text-stone-50 px-8 py-4 text-sm font-medium hover:bg-slate-800 transition-colors">
              {c.ctaLabel}
              <ArrowRight size={16} />
            </Link>
            <Link data-testid="blog-article-back" to="/blog" className="inline-flex items-center gap-2 text-sm text-stone-800 border-b border-stone-300 hover:border-stone-900 pb-1 transition-colors">
              {c.more} <ArrowRight size={13} />
            </Link>
          </div>
        </section>
      </article>
    </PageShell>
  );
}
